import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ArrowLeft,
  CheckCircle2,
  CreditCard,
  LoaderCircle,
  MapPin,
  PackageCheck,
  Store,
  Wallet,
  Smartphone,
  Building2,
  Upload,
  ShoppingBag,
  AlertCircle,
} from "lucide-react";

import { Link } from "react-router-dom";
import Navbar from "../../components/common/Navbar";
import Footer from "../../components/common/Footer";
import "../../styles/checkout.css";

const API_BASE_URL = (
  process.env.REACT_APP_API_URL ||
  "https://api.laesquinadelparques.com/api"
).replace(/\/+$/, "");

const API_ORIGIN = API_BASE_URL.replace(/\/api\/?$/, "");

const PAYMENT_METHODS = {
  nequi: "nequi",
  transfer: "transfer",
  cash_store: "cash_store",
  cash_delivery: "cash_delivery",
};

const money = (value) =>
  new Intl.NumberFormat("es-CO", {
    style: "currency",
    currency: "COP",
    maximumFractionDigits: 0,
  }).format(Number(value) || 0);

/**
 * Convierte valores a texto seguro para React.
 * Evita el error "Objects are not valid as a React child".
 */
function safeText(value, fallback = "") {
  if (value === undefined || value === null) return fallback;

  if (
    typeof value === "string" ||
    typeof value === "number"
  ) {
    return String(value);
  }

  if (typeof value === "boolean") {
    return value ? "Sí" : "No";
  }

  if (typeof value === "object") {
    const candidate =
      value.label ??
      value.name ??
      value.title ??
      value.text ??
      value.message ??
      value.description ??
      value.value;

    if (
      candidate !== undefined &&
      candidate !== null &&
      typeof candidate !== "object"
    ) {
      return String(candidate);
    }
  }

  return fallback;
}

function readStoredAuth() {
  try {
    return JSON.parse(localStorage.getItem("auth") || "{}");
  } catch {
    return {};
  }
}

function readStoredCart() {
  try {
    const parsed = JSON.parse(localStorage.getItem("cart") || "[]");

    if (Array.isArray(parsed)) return parsed;

    if (Array.isArray(parsed?.items)) return parsed.items;
    if (Array.isArray(parsed?.cart)) return parsed.cart;

    return [];
  } catch {
    return [];
  }
}

function getProductId(item) {
  return (
    item?.product_id ??
    item?.product?.id ??
    item?.product?.pk ??
    item?.id ??
    item?.pk ??
    null
  );
}

function getQuantity(item) {
  return Math.max(
    1,
    Number(item?.quantity ?? item?.qty ?? item?.cantidad ?? 1)
  );
}

function getPrice(item) {
  return Number(
    item?.unit_price ??
      item?.price ??
      item?.sale_price ??
      item?.product?.price ??
      item?.product?.sale_price ??
      0
  );
}

function getImage(item) {
  const image =
    item?.image_url ||
    item?.image ||
    item?.product?.image_url ||
    item?.product?.image ||
    "";

  return typeof image === "string" ? image : "";
}

function getName(item) {
  return safeText(
    item?.product_name ||
      item?.name ||
      item?.title ||
      item?.product?.name ||
      item?.product?.title,
    "Producto"
  );
}

/**
 * Obtiene el primer valor configurado, admitiendo valores simples
 * y objetos anidados como los que devuelve Django.
 */
function getSetting(settings, ...keys) {
  for (const key of keys) {
    const value = settings?.[key];

    if (value !== undefined && value !== null && value !== "") {
      return value;
    }
  }

  return "";
}



/**
 * Resuelve una URL de imagen o QR, tanto absoluta como relativa.
 */
function getImageUrl(value) {
  if (!value) return "";

  let url = "";

  if (typeof value === "string") {
    url = value.trim();
  } else if (typeof value === "object") {
    url =
      value.qr_official_url ||
      value.qr_url ||
      value.qr_generated_url ||
      value.image_url ||
      value.image ||
      value.url ||
      value.src ||
      value.file ||
      value.path ||
      "";
  }

  if (typeof url !== "string" || !url.trim()) return "";

  url = url.trim();

  if (
    url.startsWith("data:") ||
    url.startsWith("blob:") ||
    /^https?:\/\//i.test(url)
  ) {
    return url;
  }

  if (url.startsWith("//")) {
    return `${window.location.protocol}${url}`;
  }

  return `${API_ORIGIN}${url.startsWith("/") ? "" : "/"}${url}`;
}



function getResponseOrder(data) {
  if (!data || typeof data !== "object") return null;

  return data.order || data.checkout || data;
}

async function apiRequest(path, options = {}) {
  const auth = readStoredAuth();

  const token =
    auth?.access ||
    auth?.access_token ||
    auth?.token ||
    auth?.user?.access ||
    "";

  const isFormData =
    typeof FormData !== "undefined" &&
    options.body instanceof FormData;

  const headers = {
    ...(isFormData ? {} : { "Content-Type": "application/json" }),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...options.headers,
  };

  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...options,
    headers,
  });

  const raw = await response.text();

  let data = {};

  try {
    data = raw ? JSON.parse(raw) : {};
  } catch {
    data = { detail: raw };
  }

  if (!response.ok) {
    const detail =
      data?.detail ||
      data?.message ||
      data?.error ||
      Object.entries(data || {})
        .map(([key, value]) => {
          const message = Array.isArray(value)
            ? value.map((entry) => safeText(entry)).join(" ")
            : typeof value === "string"
              ? value
              : JSON.stringify(value);

          return `${key}: ${message}`;
        })
        .join(" · ") ||
      `Error HTTP ${response.status}`;

    throw new Error(safeText(detail, `Error HTTP ${response.status}`));
  }

  return data;
}

export default function Checkout() {
  const [cart, setCart] = useState(() => readStoredCart());
  const [settings, setSettings] = useState(null);

  const [deliveryType, setDeliveryType] = useState("store");
  const [paymentMethod, setPaymentMethod] = useState(
    PAYMENT_METHODS.nequi
  );

  const [customerName, setCustomerName] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");

  const [address, setAddress] = useState("");
  const [city, setCity] = useState("Medellín");
  const [department, setDepartment] = useState("Antioquia");
  const [addressReference, setAddressReference] = useState("");
  const [pickupStore, setPickupStore] = useState(
    "La Esquina del Parqués"
  );
  const [customerNotes, setCustomerNotes] = useState("");

  const [paymentReference, setPaymentReference] = useState("");
  const [paymentProof, setPaymentProof] = useState(null);

  const [loading, setLoading] = useState(false);
  const [loadingSettings, setLoadingSettings] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [createdOrder, setCreatedOrder] = useState(null);

  const auth = useMemo(() => readStoredAuth(), []);

  useEffect(() => {
    const user = auth?.user || auth?.profile || {};

    setCustomerName(
      user?.full_name ||
        user?.name ||
        [user?.first_name, user?.last_name].filter(Boolean).join(" ") ||
        ""
    );

    setCustomerEmail(user?.email || "");
    setCustomerPhone(user?.phone || user?.telephone || "");
  }, [auth]);

  const loadPaymentSettings = useCallback(async () => {
    setLoadingSettings(true);
    setError("");

    try {
      const data = await apiRequest("/checkout/payment-settings/");

      const paymentSettings =
        data?.settings ||
        data?.payment_settings ||
        data?.data?.settings ||
        data?.data ||
        data;

      setSettings(
        paymentSettings && typeof paymentSettings === "object"
          ? paymentSettings
          : {}
      );
    } catch (err) {
      setSettings(null);
      setError(
        `No se pudo cargar la configuración de pagos: ${err.message}`
      );
    } finally {
      setLoadingSettings(false);
    }
  }, []);

  useEffect(() => {
    loadPaymentSettings();
  }, [loadPaymentSettings]);

  const subtotal = useMemo(
    () =>
      cart.reduce(
        (sum, item) => sum + getPrice(item) * getQuantity(item),
        0
      ),
    [cart]
  );

  // El backend debe calcular y confirmar el domicilio definitivo.
  const shippingCost = 0;
  const total = Math.max(0, subtotal + shippingCost);

  /*
   * Compatibilidad con dos estructuras:
   *
   * Estructura antigua:
   * { nequi_number: "...", nequi_holder: "..." }
   *
   * Estructura anidada:
   * {
   *   nequi: {
   *     enabled: true,
   *     number: "...",
   *     holder: "...",
   *     qr_url: "..."
   *   }
   * }
   */
  const nequiConfig =
    settings?.nequi && typeof settings.nequi === "object"
      ? settings.nequi
      : {};

  const bankConfig =
    settings?.bank_account &&
    typeof settings.bank_account === "object"
      ? settings.bank_account
      : settings?.bank &&
          typeof settings.bank === "object"
        ? settings.bank
        : {};

  const nequiNumber = safeText(
    getSetting(settings, "nequi_number", "nequi_phone") ||
      nequiConfig.number ||
      nequiConfig.phone,
    ""
  );

  const nequiHolder = safeText(
    getSetting(settings, "nequi_holder", "nequi_account_holder") ||
      nequiConfig.holder ||
      nequiConfig.account_holder,
    ""
  );

  const bankName = safeText(
    getSetting(settings, "bank_name") ||
      bankConfig.bank_name ||
      bankConfig.name ||
      bankConfig.bank,
    ""
  );

  const bankAccountType = safeText(
    getSetting(settings, "bank_account_type", "account_type") ||
      bankConfig.account_type ||
      bankConfig.type,
    "Cuenta de ahorros"
  );

  const bankAccountNumber = safeText(
    getSetting(settings, "bank_account_number", "account_number") ||
      bankConfig.account_number ||
      bankConfig.number,
    ""
  );

  const bankHolder = safeText(
    getSetting(settings, "bank_account_holder", "account_holder") ||
      bankConfig.holder ||
      bankConfig.account_holder,
    ""
  );

  const nequiEnabled =
    settings?.nequi_enabled !== false &&
    nequiConfig?.enabled !== false &&
    Boolean(nequiNumber);

  const bankEnabled =
    settings?.bank_transfer_enabled !== false &&
    bankConfig?.enabled !== false &&
    Boolean(bankAccountNumber);

  const cashEnabled = settings?.cash_enabled === true;

  const cashMaxAmount = Number(settings?.cash_max_amount || 0);

  const cashMethod =
    deliveryType === "home"
      ? PAYMENT_METHODS.cash_delivery
      : PAYMENT_METHODS.cash_store;

  const cashAllowedByAmount =
    !cashMaxAmount || total <= cashMaxAmount;

  const availableMethods = useMemo(() => {
    const methods = [];

    if (nequiEnabled) {
      methods.push({
        id: PAYMENT_METHODS.nequi,
        title: "Nequi",
        description: "Paga desde tu cuenta Nequi.",
        icon: Smartphone,
      });
    }

    if (bankEnabled) {
      methods.push({
        id: PAYMENT_METHODS.transfer,
        title: "Cuenta de ahorros",
        description: "Realiza una transferencia bancaria.",
        icon: Building2,
      });
    }

    if (cashEnabled && cashAllowedByAmount) {
      methods.push({
        id: cashMethod,
        title: "Efectivo",
        description:
          deliveryType === "home"
            ? "Paga al recibir tu pedido."
            : "Paga al recoger tu pedido en la tienda.",
        icon: Wallet,
      });
    }

    return methods;
  }, [
    nequiEnabled,
    bankEnabled,
    cashEnabled,
    cashAllowedByAmount,
    cashMethod,
    deliveryType,
  ]);

  useEffect(() => {
    if (
      availableMethods.length > 0 &&
      !availableMethods.some((method) => method.id === paymentMethod)
    ) {
      setPaymentMethod(availableMethods[0].id);
    }
  }, [availableMethods, paymentMethod]);

  const isCashPayment = paymentMethod.startsWith("cash_");

  const isTransferPayment =
    paymentMethod === PAYMENT_METHODS.transfer ||
    paymentMethod === PAYMENT_METHODS.nequi;

  const updateCart = (nextCart) => {
    setCart(nextCart);

    try {
      localStorage.setItem("cart", JSON.stringify(nextCart));
    } catch {
      // El estado de React sigue actualizado aunque localStorage falle.
    }
  };

  const removeItem = (index) => {
    updateCart(cart.filter((_, itemIndex) => itemIndex !== index));
  };

  const validateForm = () => {
    if (!cart.length) return "Tu carrito está vacío.";
    if (!customerName.trim()) return "Escribe el nombre del comprador.";
    if (!customerPhone.trim()) return "Escribe un teléfono de contacto.";

    const missingProductId = cart.some((item) => !getProductId(item));

    if (missingProductId) {
      return (
        "Uno o más productos del carrito no tienen un identificador válido. " +
        "Actualiza el carrito antes de continuar."
      );
    }

    if (deliveryType === "home") {
      if (!address.trim()) return "Escribe la dirección de entrega.";
      if (!city.trim()) return "Escribe la ciudad.";
    }

    if (!availableMethods.some((method) => method.id === paymentMethod)) {
      return "Selecciona un método de pago habilitado.";
    }

    return "";
  };

  const submitCheckout = async (event) => {
    event.preventDefault();

    setError("");
    setSuccess("");

    if (createdOrder) {
      setError("Este pedido ya fue creado. No es necesario enviarlo otra vez.");
      return;
    }

    const validationError = validateForm();

    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);

    try {
      // 1. Sincronizar el carrito con Django.
      await apiRequest("/cart/sync/", {
        method: "POST",
        body: JSON.stringify({
          items: cart.map((item) => ({
            product_id: getProductId(item),
            quantity: getQuantity(item),
          })),
        }),
      });

      // 2. Preparar los datos del pedido.
      const checkoutPayload = {
        customer_name: customerName.trim(),
        customer_phone: customerPhone.trim(),
        customer_email: customerEmail.trim(),
        delivery_type: deliveryType === "home" ? "home" : "store",
        payment_method: paymentMethod,
        payment_reference: paymentReference.trim(),
        customer_notes: customerNotes.trim(),
        address: deliveryType === "home" ? address.trim() : "",
        city: deliveryType === "home" ? city.trim() : "",
        department: deliveryType === "home" ? department.trim() : "",
        address_reference:
          deliveryType === "home" ? addressReference.trim() : "",
        pickup_store_name: deliveryType === "store" ? pickupStore : "",
      };

      // 3. Crear el pedido. El servidor debe calcular los precios definitivos.
      const responseData = await apiRequest("/checkout/", {
        method: "POST",
        body: JSON.stringify(checkoutPayload),
      });

      const order = getResponseOrder(responseData);

      const orderId =
        responseData?.id ??
        responseData?.checkout?.id ??
        responseData?.order?.id ??
        responseData?.checkout_id ??
        order?.id ??
        order?.pk;

      if (!orderId) {
        throw new Error(
          "El servidor respondió, pero no devolvió el identificador del pedido."
        );
      }

      setCreatedOrder({
        ...order,
        id: orderId,
      });

      const orderNumber =
        safeText(
          order?.checkout_number ||
            order?.order_number ||
            responseData?.checkout_number ||
            responseData?.order_number,
          ""
        ) || `#${orderId}`;

      setSuccess(`Pedido ${orderNumber} creado correctamente.`);

      // 4. Enviar el comprobante si el cliente adjuntó uno.
      if (paymentProof && isTransferPayment) {
        const formData = new FormData();

        formData.append("payment_proof", paymentProof);

        if (paymentReference.trim()) {
          formData.append("payment_reference", paymentReference.trim());
        }

        try {
          await apiRequest(`/checkout/${orderId}/payment-proof/`, {
            method: "POST",
            body: formData,
          });

          setSuccess(
            `Pedido ${orderNumber} creado. Comprobante enviado para revisión.`
          );
        } catch (proofError) {
          setError(
            `El pedido ${orderNumber} fue creado, pero no se pudo enviar el comprobante: ${proofError.message}. Puedes conservar el pedido y volver a enviar el comprobante desde el sistema.`
          );
        }
      }

      // 5. Limpiar el carrito después de crear el pedido.
      updateCart([]);
    } catch (err) {
      setError(err.message || "No se pudo crear el pedido.");
    } finally {
      setLoading(false);
    }
  };

  /*
   * Obtener los QR tanto de la estructura antigua como de la anidada.
   * Ejemplo: settings.nequi.qr_url
   */
  const paymentQrValue =
    paymentMethod === PAYMENT_METHODS.nequi
      ? nequiConfig.qr_official_url ||
        nequiConfig.qr_url ||
        nequiConfig.qr_generated_url ||
        settings?.nequi_qr_official_url ||
        settings?.nequi_qr_official ||
        settings?.nequi_qr_url
      : bankConfig.qr_official_url ||
        bankConfig.qr_url ||
        bankConfig.qr_generated_url ||
        settings?.bancolombia_qr_official_url ||
        settings?.bancolombia_qr_official ||
        settings?.bank_qr_url ||
        settings?.qr_image;

  const paymentQr = getImageUrl(paymentQrValue);

  const paymentInstructionsValue = getSetting(
    settings,
    "payment_instructions",
    "instructions"
  );

  const paymentInstructions = safeText(
    paymentInstructionsValue,
    ""
  );

  const orderStatus = safeText(
    createdOrder?.status ||
      createdOrder?.checkout?.status ||
      createdOrder?.order?.status,
    "Pendiente de confirmación"
  );

  return (
    <>
      <Navbar />

      <main className="checkout-page">
        <div className="checkout-container">
          <Link to="/carrito" className="checkout-back">
            <ArrowLeft size={18} />
            Volver al carrito
          </Link>

          <header className="checkout-heading">
            <span className="checkout-eyebrow">
              LA ESQUINA DEL PARQUÉS
            </span>

            <h1>Finaliza tu compra</h1>

            <p>
              Elige cómo recibir tu pedido y selecciona tu forma de pago.
            </p>
          </header>

          {error && (
            <div
              className="checkout-alert checkout-alert-error"
              role="alert"
            >
              <AlertCircle size={20} />
              <span>{error}</span>
            </div>
          )}

          {success && (
            <div
              className="checkout-alert checkout-alert-success"
              role="status"
            >
              <CheckCircle2 size={20} />

              <div>
                <strong>{success}</strong>

                {createdOrder && (
                  <p>Estado: {orderStatus}</p>
                )}
              </div>
            </div>
          )}

          <form className="checkout-layout" onSubmit={submitCheckout}>
            <section className="checkout-main">
              {/* ENTREGA */}
              <section className="checkout-card">
                <div className="checkout-section-title">
                  <span className="checkout-step">1</span>

                  <div>
                    <h2>¿Cómo quieres recibir tu pedido?</h2>
                    <p>Selecciona una modalidad de entrega.</p>
                  </div>
                </div>

                <div className="checkout-delivery-options">
                  <button
                    type="button"
                    className={`checkout-delivery-option ${
                      deliveryType === "store" ? "is-selected" : ""
                    }`}
                    onClick={() => {
                      setDeliveryType("store");
                      setError("");
                    }}
                    aria-pressed={deliveryType === "store"}
                  >
                    <span className="checkout-option-icon">
                      <Store size={24} />
                    </span>

                    <span className="checkout-option-content">
                      <strong>Recoger en tienda</strong>

                      <small>
                        Recoge tu pedido directamente en nuestro punto de
                        atención.
                      </small>

                      <span className="checkout-option-tag">
                        Sin costo de domicilio
                      </span>
                    </span>

                    <span className="checkout-radio" />
                  </button>

                  <button
                    type="button"
                    className={`checkout-delivery-option ${
                      deliveryType === "home" ? "is-selected" : ""
                    }`}
                    onClick={() => {
                      setDeliveryType("home");
                      setError("");
                    }}
                    aria-pressed={deliveryType === "home"}
                  >
                    <span className="checkout-option-icon">
                      <MapPin size={24} />
                    </span>

                    <span className="checkout-option-content">
                      <strong>Recibir a domicilio</strong>

                      <small>
                        Recibe tu compra en la dirección que nos indiques.
                      </small>

                      <span className="checkout-option-tag">
                        El domicilio se valida en el pedido
                      </span>
                    </span>

                    <span className="checkout-radio" />
                  </button>
                </div>

                {deliveryType === "store" ? (
                  <div className="checkout-pickup-panel">
                    <PackageCheck size={23} />

                    <div>
                      <strong>Tu pedido estará listo para recoger</strong>

                      <p>
                        Punto de recogida: La Esquina del Parqués. El
                        personal te confirmará la disponibilidad del pedido.
                      </p>

                      <label htmlFor="pickupStore">
                        Punto de recogida
                      </label>

                      <select
                        id="pickupStore"
                        value={pickupStore}
                        onChange={(event) =>
                          setPickupStore(event.target.value)
                        }
                      >
                        <option value="La Esquina del Parqués">
                          La Esquina del Parqués
                        </option>
                      </select>
                    </div>
                  </div>
                ) : (
                  <div className="checkout-fields">
                    <label>
                      Dirección de entrega *
                      <input
                        value={address}
                        onChange={(event) =>
                          setAddress(event.target.value)
                        }
                        placeholder="Calle, carrera, número y barrio"
                        required
                      />
                    </label>

                    <div className="checkout-fields-row">
                      <label>
                        Ciudad *
                        <input
                          value={city}
                          onChange={(event) => setCity(event.target.value)}
                          required
                        />
                      </label>

                      <label>
                        Departamento
                        <input
                          value={department}
                          onChange={(event) =>
                            setDepartment(event.target.value)
                          }
                        />
                      </label>
                    </div>

                    <label>
                      Referencia para llegar
                      <textarea
                        value={addressReference}
                        onChange={(event) =>
                          setAddressReference(event.target.value)
                        }
                        placeholder="Apartamento, portería, indicaciones..."
                        rows={2}
                      />
                    </label>
                  </div>
                )}
              </section>

              {/* COMPRADOR */}
              <section className="checkout-card">
                <div className="checkout-section-title">
                  <span className="checkout-step">2</span>

                  <div>
                    <h2>Datos del comprador</h2>
                    <p>Usaremos estos datos para confirmar tu pedido.</p>
                  </div>
                </div>

                <div className="checkout-fields">
                  <label>
                    Nombre completo *
                    <input
                      value={customerName}
                      onChange={(event) =>
                        setCustomerName(event.target.value)
                      }
                      autoComplete="name"
                      required
                    />
                  </label>

                  <div className="checkout-fields-row">
                    <label>
                      Teléfono *
                      <input
                        value={customerPhone}
                        onChange={(event) =>
                          setCustomerPhone(event.target.value)
                        }
                        type="tel"
                        autoComplete="tel"
                        required
                      />
                    </label>

                    <label>
                      Correo electrónico
                      <input
                        value={customerEmail}
                        onChange={(event) =>
                          setCustomerEmail(event.target.value)
                        }
                        type="email"
                        autoComplete="email"
                      />
                    </label>
                  </div>

                  <label>
                    Notas del pedido
                    <textarea
                      value={customerNotes}
                      onChange={(event) =>
                        setCustomerNotes(event.target.value)
                      }
                      placeholder="Indicaciones adicionales (opcional)"
                      rows={2}
                    />
                  </label>
                </div>
              </section>

              {/* MÉTODOS DE PAGO */}
              <section className="checkout-card">
                <div className="checkout-section-title">
                  <span className="checkout-step">3</span>

                  <div>
                    <h2>Elige cómo pagar</h2>
                    <p>Selecciona una de las opciones disponibles.</p>
                  </div>
                </div>

                {loadingSettings ? (
                  <p className="checkout-loading">
                    <LoaderCircle className="checkout-spin" size={19} />
                    Cargando métodos de pago...
                  </p>
                ) : availableMethods.length === 0 ? (
                  <div className="checkout-empty-payment">
                    <AlertCircle size={21} />

                    <div>
                      <strong>
                        No hay métodos de pago disponibles.
                      </strong>

                      <p>
                        Revisa la configuración de pagos en el administrador
                        de Django.
                      </p>
                    </div>

                    <button
                      type="button"
                      onClick={loadPaymentSettings}
                      className="checkout-secondary-button"
                    >
                      Volver a cargar
                    </button>
                  </div>
                ) : (
                  <div className="checkout-payment-options">
                    {availableMethods.map((method) => {
                      const Icon = method.icon;
                      const selected = paymentMethod === method.id;

                      return (
                        <button
                          type="button"
                          key={method.id}
                          className={`checkout-payment-option ${
                            selected ? "is-selected" : ""
                          }`}
                          onClick={() => {
                            setPaymentMethod(method.id);
                            setError("");
                          }}
                          aria-pressed={selected}
                        >
                          <span className="checkout-option-icon">
                            <Icon size={23} />
                          </span>

                          <span className="checkout-option-content">
                            <strong>{method.title}</strong>
                            <small>{method.description}</small>
                          </span>

                          <span className="checkout-radio" />
                        </button>
                      );
                    })}
                  </div>
                )}

                {/* NEQUI */}
                {paymentMethod === PAYMENT_METHODS.nequi && (
                  <div className="checkout-payment-details">
                    <h3>Datos para pagar con Nequi</h3>

                    <div className="checkout-payment-data">
                      <span>Número Nequi</span>
                      <strong>
                        {nequiNumber || "No configurado"}
                      </strong>
                    </div>

                    <div className="checkout-payment-data">
                      <span>Titular</span>
                      <strong>
                        {nequiHolder || "Consulta con la tienda"}
                      </strong>
                    </div>

                    <p>
                      Realiza el pago por el valor total y registra la
                      referencia si la tienes. El pedido puede quedar
                      pendiente hasta que el pago sea verificado.
                    </p>
                  </div>
                )}

                {/* TRANSFERENCIA */}
                {paymentMethod === PAYMENT_METHODS.transfer && (
                  <div className="checkout-payment-details">
                    <h3>Datos de la cuenta de ahorros</h3>

                    <div className="checkout-payment-data">
                      <span>Banco</span>
                      <strong>{bankName || "No configurado"}</strong>
                    </div>

                    <div className="checkout-payment-data">
                      <span>Tipo de cuenta</span>
                      <strong>{bankAccountType}</strong>
                    </div>

                    <div className="checkout-payment-data">
                      <span>Número de cuenta</span>
                      <strong>
                        {bankAccountNumber || "No configurado"}
                      </strong>
                    </div>

                    <div className="checkout-payment-data">
                      <span>Titular</span>
                      <strong>
                        {bankHolder || "Consulta con la tienda"}
                      </strong>
                    </div>

                    <p>
                      Verifica los datos antes de transferir. La confirmación
                      del pedido puede depender de la revisión del pago.
                    </p>
                  </div>
                )}

                {/* CÓDIGO QR */}
                {paymentQr && isTransferPayment && (
                  <div className="checkout-qr-container">
                    <p>QR de pago</p>

                    <img
                      src={paymentQr}
                      alt="Código QR para realizar el pago"
                      style={{
                        display: "block",
                        width: "220px",
                        maxWidth: "100%",
                        height: "auto",
                        objectFit: "contain",
                      }}
                    />
                  </div>
                )}

                {/* EFECTIVO */}
                {isCashPayment && (
                  <div className="checkout-payment-details">
                    <h3>Pago en efectivo</h3>

                    <p>
                      {deliveryType === "home"
                        ? "El pago se realizará al recibir el pedido, sujeto a la validación del comercio."
                        : "Paga en el punto de recogida al recibir la confirmación de que tu pedido está listo."}
                    </p>

                    {cashMaxAmount > 0 && (
                      <p>
                        Límite de efectivo configurado:{" "}
                        {money(cashMaxAmount)}.
                      </p>
                    )}
                  </div>
                )}

                {paymentInstructions && (
                  <div className="checkout-payment-instructions">
                    {paymentInstructions}
                  </div>
                )}

                {/* REFERENCIA Y COMPROBANTE */}
                {isTransferPayment && (
                  <div className="checkout-fields">
                    <label>
                      Referencia de pago (opcional)
                      <input
                        value={paymentReference}
                        onChange={(event) =>
                          setPaymentReference(event.target.value)
                        }
                        placeholder="Número de transacción"
                      />
                    </label>

                    <label className="checkout-upload">
                      <Upload size={19} />

                      <span>
                        <strong>Adjuntar comprobante</strong>
                        <small>
                          Imagen JPG, PNG o WEBP (opcional)
                        </small>
                      </span>

                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        onChange={(event) => {
                          const file = event.target.files?.[0] || null;

                          if (file && file.size > 10 * 1024 * 1024) {
                            setError(
                              "El comprobante no puede superar los 10 MB."
                            );
                            event.target.value = "";
                            setPaymentProof(null);
                            return;
                          }

                          setError("");
                          setPaymentProof(file);
                        }}
                      />
                    </label>

                    {paymentProof && (
                      <p className="checkout-file-selected">
                        <CheckCircle2 size={17} />
                        {paymentProof.name}

                        <button
                          type="button"
                          onClick={() => setPaymentProof(null)}
                        >
                          Quitar
                        </button>
                      </p>
                    )}
                  </div>
                )}
              </section>
            </section>

            {/* RESUMEN DE COMPRA */}
            <aside className="checkout-summary">
              <div className="checkout-summary-header">
                <ShoppingBag size={22} />
                <h2>Resumen de compra</h2>
              </div>

              <div className="checkout-summary-items">
                {cart.length === 0 ? (
                  <p className="checkout-cart-empty">
                    No hay productos en tu carrito.
                  </p>
                ) : (
                  cart.map((item, index) => (
                    <div
                      className="checkout-summary-item"
                      key={item?.id ?? getProductId(item) ?? index}
                    >
                      {getImage(item) && (
                        <img
                          src={getImage(item)}
                          alt={getName(item)}
                          className="checkout-product-image"
                        />
                      )}

                      <div className="checkout-product-info">
                        <strong>{getName(item)}</strong>

                        <small>
                          Cantidad: {getQuantity(item)} ×{" "}
                          {money(getPrice(item))}
                        </small>

                        <button
                          type="button"
                          className="checkout-remove-item"
                          disabled={loading || Boolean(createdOrder)}
                          onClick={() => removeItem(index)}
                        >
                          Quitar
                        </button>
                      </div>

                      <strong className="checkout-item-total">
                        {money(getPrice(item) * getQuantity(item))}
                      </strong>
                    </div>
                  ))
                )}
              </div>

              <div className="checkout-summary-line">
                <span>Subtotal</span>
                <strong>{money(subtotal)}</strong>
              </div>

              <div className="checkout-summary-line">
                <span>Domicilio</span>
                <strong>
                  {deliveryType === "store" ? "Gratis" : "Por confirmar"}
                </strong>
              </div>

              <div className="checkout-summary-total">
                <span>Total estimado</span>
                <strong>{money(total)}</strong>
              </div>

              <p className="checkout-summary-note">
                El total definitivo y la disponibilidad de los productos se
                validan en el servidor al crear el pedido.
              </p>

              <button
                type="submit"
                className="checkout-pay-button"
                disabled={
                  loading ||
                  loadingSettings ||
                  cart.length === 0 ||
                  availableMethods.length === 0 ||
                  Boolean(createdOrder)
                }
              >
                {loading ? (
                  <>
                    <LoaderCircle className="checkout-spin" size={21} />
                    Creando pedido...
                  </>
                ) : createdOrder ? (
                  <>
                    <CheckCircle2 size={21} />
                    Pedido creado
                  </>
                ) : (
                  <>
                    <CreditCard size={21} />
                    Pagar y crear pedido
                  </>
                )}
              </button>

              <p className="checkout-secure-note">
                Revisa tus datos antes de confirmar. No compartas claves ni
                códigos de seguridad bancarios.
              </p>

              {createdOrder && (
                <div className="checkout-order-created">
                  <CheckCircle2 size={23} />

                  <div>
                    <strong>Pedido registrado</strong>

                    <p>
                      Conserva el número de pedido para consultar su estado.
                    </p>
                  </div>
                </div>
              )}
            </aside>
          </form>
        </div>
      </main>

      <Footer />
    </>
  );
}
